import config from "@Config/index";
import axios from "axios";
import urlJoin from "helpers/urljoin";
import { isEmptyObject } from "helpers/validations";

export interface University {
  id: string;
  name: string;
  location: {
    lat: number;
    lng: number;
  };
}

export interface LocationCoordinates {
  lat: number;
  lng: number;
}

export interface DistanceResult {
  distance: string;
  duration: string;
}

export interface DistanceObject {
  [key: string]: DistanceResult;
}

export interface InventoryObject {
  location?: {
    location_coordinates?: LocationCoordinates;
  };
}

export type TravelMode = "DRIVING" | "TRANSIT" | "WALKING";

// Map of marker types to their icon paths
const MARKER_ICON_MAP: Record<string, string> = {
  Universities: "maps_nearby_universities.svg",
  "Bus Stations": "maps_bus_stops.svg",
  "Train Stations": "maps_train_stations.svg",
  Supermarkets: "maps_shopping.svg",
  Pharmacies: "maps_pharmacy.svg",
  Restaurants: "maps_restaurants.svg",
  Hospitals: "maps_hospital.svg",
  Police: "maps_police.svg",
  Polices: "maps_police.svg",
  "Shopping Malls": "maps_shopping_mall.svg",
  Property: "maps_current_property.svg",
  "Active Marker": "maps_nearby_universities.svg",
};

export const getMapMarkerIconUrl = (name: string, isSelected = false): string => {
  if (isSelected) {
    return `${config.IMAGE_STATIC_URL}/assets/maps_active_marker.svg`;
  }

  const iconName = MARKER_ICON_MAP[name] || "maps_nearby_universities.svg";
  return `${config.IMAGE_STATIC_URL}/images/${iconName}`;
};

export const getFilteredUniversityFromLocal = (
  universities: University[],
  selectedUniversityFromLocal: University | null,
): University | undefined => {
  if (!selectedUniversityFromLocal) return undefined;

  return universities?.find((uni) =>
    uni?.name
      ?.toLowerCase()
      ?.includes(selectedUniversityFromLocal?.name?.split(" -")[0].toLowerCase()),
  );
};

export const mapLoadedChina = (idName = "map"): boolean =>
  !!(
    typeof mapboxgl !== "undefined" &&
    mapboxgl !== null &&
    mapboxgl.maps != null &&
    mapboxgl.supported() &&
    document.getElementById(idName) != null
  );

export const createCustomMarkerElement = (
  iconUrl: string,
  labelText: string,
  markerSize: number,
): HTMLElement => {
  const markerContainer = document.createElement("div");
  markerContainer.style.cssText = "display: flex; flex-direction: column; align-items: center;";

  // Create label element
  const label = document.createElement("div");
  if (labelText) {
    label.textContent = labelText;
    // Apply styles to label
    label.style.cssText = `
      background: white;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      margin-bottom: 4px;
      box-shadow: 0 1px 3px rgba(0,0,0,0.2);
      white-space: nowrap;
      font-family: Arial, sans-serif;
      color: #333;
      display: flex;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      backface-visibility: hidden;
      transform: translateZ(0);
    `;
  } else {
    label.style.display = "none";
  }

  // Create marker image element
  const markerElement = document.createElement("div");
  markerElement.style.cssText = `
    background-image: url(${iconUrl});
    width: ${markerSize}px;
    height: ${markerSize}px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  `;

  // Append both label and marker to container
  markerContainer.appendChild(label);
  markerContainer.appendChild(markerElement);

  return markerContainer;
};

export const calculateDistance = async (
  data: any,
  inventory: InventoryObject,
  setDistanceObj: (obj: DistanceObject) => void,
): Promise<void> => {
  const originCoordinatesInventory = inventory?.location?.location_coordinates;
  const destinationCoordinates = { lat: data?.lat, lng: data?.lng };

  if (isEmptyObject(originCoordinatesInventory) || isEmptyObject(destinationCoordinates)) {
    return;
  }

  const distanceObj: DistanceObject = {};
  const travelModes: TravelMode[] = ["DRIVING", "TRANSIT", "WALKING"];

  try {
    const distancePromises = travelModes.map(async (mode) => {
      const options = {
        url: urlJoin(config.API_URL, "/api/v0/regions/distances/fetch"),
        params: {
          destinations: `${destinationCoordinates?.lat},${destinationCoordinates?.lng}`,
          origins: `${originCoordinatesInventory?.lat},${originCoordinatesInventory?.lng}`,
          mode: mode.toLowerCase(),
          units: "imperial",
        },
      };

      const { data: dataFromApi } = await axios.get(options.url, { params: options.params });
      const { data: result } = dataFromApi?.data;

      if (result?.status === "OK") {
        return {
          mode,
          data: {
            distance: result.distance.text,
            duration: result.duration.text,
          },
        };
      }
      return null;
    });

    const results = await Promise.all(distancePromises);

    // Build the distanceObj from the results

    results.forEach((item) => {
      if (item) {
        distanceObj[item.mode] = item.data;
      }
    });

    setDistanceObj(distanceObj);
  } catch (error) {
    console.error("Error fetching distances:", error);
  }
};

export const showInKmsOrMiles = (data: string, showMiles: boolean, t: any) => {
  if (showMiles) {
    const splitData = data.split(" ");
    splitData[1] = t(splitData[1]);
    data = splitData.join(" ");
    return data;
  }
  if (data === null) return null;
  const conversions: Record<string, number> = {
    ft: 0.0003048,
    mi: 1.60934,
    km: 1,
  };
  const unit = data.match(/ft|mi|km/);
  if (!unit) return null;
  const distance = parseFloat(data.replace(unit[0], ""));
  const distanceInKms = distance * conversions[unit[0]];
  return `${distanceInKms.toFixed(unit[0] === t("ft") ? 2 : 1)} ${t("km")}`;
};

// Nearby places can share a name (e.g. several "Tesco Express"), so match on coordinates too
export const isSameLocation = (item: any, selected: any) =>
  !!selected?.name &&
  item?.name === selected?.name &&
  item?.lat === selected?.lat &&
  item?.lng === selected?.lng;
